import { Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { type REITHolding } from '@/hooks/reits/useREITHoldings';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface REITHoldingSummaryProps {
  holding: REITHolding;
  typeLabel: string;
  typeLabelDescription?: string;
  sectorLabel: string;
  sectorLabelDescription?: string
}

function LabeledField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}

export function REITHoldingSummary({ holding, typeLabel, typeLabelDescription, sectorLabel, sectorLabelDescription }: REITHoldingSummaryProps) {
  return (
    <div className="space-y-4">
      {/* Heading */}
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-bold text-foreground">{holding.reit_name}</h1>
        {holding.ticker_symbol && (
          <span className="text-lg text-muted-foreground font-mono">({holding.ticker_symbol})</span>
        )}
      </div>

      {/* Type and Sector badges */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Type:</span>
          <Badge variant="secondary" className='capitalize'>
            {typeLabel}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    {typeLabelDescription}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Badge>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Sector:</span>
          <Badge variant="outline" className='capitalize'>
            <div className="flex items-center gap-2 capitalize">
              {sectorLabel}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      {sectorLabelDescription}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </Badge>
        </div>
      </div>

      {/* Required fields */}
      <div data-testid="required-fields" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 pt-2">
        <LabeledField label="Purchase Date" value={holding.purchase_date} />
        <LabeledField label="Currency" value={holding.currency} />
        <LabeledField label="Dividend Frequency" value={holding.dividend_frequency} />
        <LabeledField label="Units Held" value={holding.units_held.toLocaleString()} />
        <LabeledField label="Purchase Price / Unit" value={holding.purchase_price_per_unit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} />
        <LabeledField label="Current Price / Unit" value={holding.current_price_per_unit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} />
      </div>

      {/* Optional fields */}
      {(holding.nav_per_unit != null || holding.occupancy_rate != null || holding.notes) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
          {holding.nav_per_unit != null && (
            <div data-testid="field-nav-per-unit">
              <LabeledField
                label="NAV / Unit"
                value={holding.nav_per_unit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
              />
            </div>
          )}
          {holding.occupancy_rate != null && (
            <div data-testid="field-occupancy-rate">
              <LabeledField
                label="Occupancy Rate"
                value={`${holding.occupancy_rate.toFixed(2)}%`}
              />
            </div>
          )}
          {holding.notes && (
            <div className="flex flex-col gap-0.5 sm:col-span-2 lg:col-span-1" data-testid="field-notes">
              <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Notes</span>
              <span className="text-sm text-foreground whitespace-pre-wrap">{holding.notes}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
